import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { LicenseRequest, Track } from "@shared/schema";
import { Link } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from "recharts";
import { 
  Music, 
  FileCheck, 
  Clock, 
  AlertTriangle, 
  DollarSign,
  Users,
  Calendar,
  ArrowRight
} from "lucide-react";

// Fix the type error by adding explicit interface for StatsCard
interface StatsCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  description: string;
  className?: string;
}

function StatsCard({ title, value, icon, description, className = '' }: StatsCardProps) {
  return (
    <Card className={className}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-1">{title}</p>
            <h3 className="text-2xl font-bold">{value}</h3>
            {description && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
          </div>
          <div className="p-2 bg-primary/10 rounded-full text-primary">
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// For stats data, create a proper interface
interface StatsData {
  [key: string]: {
    value: string;
    description: string;
  }
}

export default function AdminDashboard() {
  const [timeRange, setTimeRange] = useState("month");
  
  // Fetch license requests
  const { data: licenseRequests = [], isLoading: requestsLoading } = useQuery<LicenseRequest[]>({ 
    queryKey: ['/api/license-requests'],
  });
  
  // Fetch tracks
  const { data: tracks = [], isLoading: tracksLoading } = useQuery<Track[]>({ 
    queryKey: ['/api/tracks'],
  });

  // Calculate dashboard metrics
  const pendingRequests = licenseRequests.filter(req => req.status === "pending").length;
  const approvedRequests = licenseRequests.filter(req => req.status === "approved").length;
  const totalRevenue = licenseRequests
    .filter(req => req.status === "approved" && req.amount)
    .reduce((sum, req) => sum + (req.amount || 0), 0) / 100; // Convert cents to dollars
  
  // For recent activity, sort by date and take the 5 most recent
  const recentRequests = [...licenseRequests]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  // Track usage data for chart
  const trackUsageData = tracks.map(track => ({
    name: track.title.length > 20 ? track.title.substring(0, 20) + '...' : track.title,
    count: licenseRequests.filter(req => req.tracks.includes(track.title)).length
  })).sort((a, b) => b.count - a.count).slice(0, 10);

  // Project types data for pie chart
  const projectTypeCounts = licenseRequests.reduce((acc, req) => {
    acc[req.projectType] = (acc[req.projectType] || 0) + 1;
    return acc;
  }, {});
  
  const projectTypeData = Object.entries(projectTypeCounts).map(([type, count]) => ({
    name: type === 'social' ? 'Social Media' : 
          type === 'youtube' ? 'YouTube' : 
          type === 'tv' ? 'TV Show' : 
          type === 'film' ? 'Film' : 
          type === 'podcast' ? 'Podcast' : 
          type === 'corporate' ? 'Corporate' : 
          type,
    value: count
  }));

  const COLORS = ['#7928ca', '#8b3dff', '#9f67e3', '#6e24de', '#4c18a3'];

  // Revenue by month data
  const revenueData = [
    { name: 'Jan', revenue: 4200 },
    { name: 'Feb', revenue: 3800 },
    { name: 'Mar', revenue: 5100 },
    { name: 'Apr', revenue: 4700 },
    { name: 'May', revenue: 6200 },
    { name: 'Jun', revenue: 5900 },
  ];

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  // Status badge component
  const StatusBadge = ({ status }: { status: string }) => {
    let color = "";
    
    switch (status) {
      case "approved":
        color = "bg-green-100 text-green-800";
        break;
      case "pending":
        color = "bg-yellow-100 text-yellow-800";
        break;
      case "rejected":
        color = "bg-red-100 text-red-800";
        break;
      default:
        color = "bg-gray-100 text-gray-800";
    }
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${color}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // ... then initialize stats data properly ...
  const statsData: StatsData = {
    // ... existing code ...
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Admin Dashboard</h1>
        <div className="space-x-2">
          <Button variant="outline" size="sm">Export Data</Button>
          <Link href="/admin/requests">
            <Button size="sm">Manage Requests</Button>
          </Link>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard 
          title="Total Tracks" 
          value={tracks.length.toString()} 
          icon={<Music className="h-5 w-5" />} 
          description="Total number of tracks in the library"
        />
        <StatsCard 
          title="Approved Licenses" 
          value={approvedRequests.toString()} 
          icon={<FileCheck className="h-5 w-5" />} 
          description="Number of approved license requests"
        />
        <StatsCard 
          title="Pending Requests" 
          value={pendingRequests.toString()} 
          icon={<Clock className="h-5 w-5" />} 
          className={pendingRequests > 0 ? "border-yellow-500" : ""}
          description="Number of pending license requests"
        />
        <StatsCard 
          title="Total Revenue" 
          value={`$${totalRevenue.toLocaleString()}`}
          icon={<DollarSign className="h-5 w-5" />}
          description="Total revenue from all license sales"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Popular Tracks</CardTitle>
            <CardDescription>Top 10 most licensed tracks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={trackUsageData}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" width={100} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#7928ca" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Project Types</CardTitle>
            <CardDescription>Distribution of licensing by project type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={projectTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {projectTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Latest license requests</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {requestsLoading ? (
              <div className="text-center py-4">Loading recent activity...</div>
            ) : recentRequests.length === 0 ? (
              <div className="text-center py-4">No recent license requests</div>
            ) : (
              recentRequests.map((request) => (
                <div key={request.id} className="flex items-center justify-between border-b pb-3">
                  <div>
                    <p className="font-medium">{request.name}</p>
                    <div className="flex space-x-4 text-sm text-muted-foreground">
                      <span>{formatDate(request.createdAt.toString())}</span>
                      <span>• {request.projectType === 'social' ? 'Social Media' : 
                                request.projectType === 'youtube' ? 'YouTube' : 
                                request.projectType === 'tv' ? 'TV Show' : 
                                request.projectType === 'film' ? 'Film/Movie' : 
                                request.projectType}</span>
                      <span>• {request.tracks.length} track{request.tracks.length !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <StatusBadge status={request.status} />
                    <Link href={`/admin/requests?id=${request.id}`}>
                      <Button variant="ghost" size="sm">
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="mt-4 flex justify-center">
            <Link href="/admin/requests">
              <Button variant="outline">View All Requests</Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
